import { useEffect, useState, type FormEvent } from 'react';
import PersonAvatar from './PersonAvatar.tsx';
import { api, errorMessage } from '../lib/api.ts';
import { currentUser } from '../lib/me.ts';
import type { Person } from '../../shared/organisation.ts';

/**
 * Who can sign in to this studio — the ⚙ drawer's People section.
 *
 * `people.json` is the sign-in allowlist, so this list is the access control
 * and not merely a set of labels for deck owners. That is why it lives in the
 * settings console rather than beside the folders: it is a fact about the
 * server, and it has to be reachable from every surface rather than from the
 * decks page alone.
 *
 * Three verbs, and no more on purpose. **Add** somebody by address; **correct**
 * an address that was typed wrong; **archive** to take access away. Deleting is
 * absent because it is the one action that touches decks — their owner ids live
 * on the decks themselves — and archiving already does everything revoking
 * needs, while keeping their name on the work they made.
 *
 * All three are for admins only (`STUDIO_ADMINS` on the deploy). Everyone else
 * sees the list and no controls. That is a rendering decision, never the
 * permission: the routes refuse independently, because a hidden button stops
 * nobody who can open devtools.
 *
 * A person is added with their ADDRESS as their name. Nobody should have to
 * know how a colleague spells theirs to let them in, and Google's own claim
 * replaces the placeholder the first time they sign in.
 */
export default function PeoplePanel() {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; email: string } | null>(null);
  const [admin, setAdmin] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get<{ people: Person[] }>('/api/people')
      .then((r) => live && setPeople(r.people))
      .catch((err) => live && setError(errorMessage(err)));
    currentUser().then((me) => live && setAdmin(!!me?.isAdmin));
    return () => {
      live = false;
    };
  }, []);

  /** Every write answers with the whole list, so there is no local patching to
   *  drift out of step with the server. */
  async function run(work: () => Promise<{ people: Person[] }>) {
    setBusy(true);
    setError(null);
    try {
      setPeople((await work()).people);
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** The address as typed is refused if anybody already holds it: two records
   *  on one address make that account unable to sign in AT ALL, because the
   *  gate refuses an ambiguous match rather than picking one. */
  function claimedBy(email: string, exceptId?: string): Person | undefined {
    const wanted = email.trim().toLowerCase();
    return (people ?? []).find((p) => p.email === wanted && p.id !== exceptId);
  }

  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = draft.trim().toLowerCase();
    if (!email.includes('@')) return setError('That does not look like an email address.');
    const clash = claimedBy(email);
    if (clash) return setError(`${clash.name} already has that address.`);
    // Name AND email: `name` is required by the registry, and the address
    // standing in for it is what the first sign-in replaces.
    const ok = await run(() => api.post<{ people: Person[] }>('/api/people', { name: email, email }));
    if (ok) setDraft('');
  }

  async function saveEmail(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!editing) return;
    const email = editing.email.trim().toLowerCase();
    if (!email.includes('@')) return setError('That does not look like an email address.');
    const clash = claimedBy(email, editing.id);
    if (clash) return setError(`${clash.name} already has that address.`);
    const ok = await run(() => api.put<{ people: Person[] }>(`/api/people/${editing.id}`, { email }));
    if (ok) setEditing(null);
  }

  function setArchived(person: Person, archived: boolean) {
    void run(() => api.put<{ people: Person[] }>(`/api/people/${person.id}`, { archived }));
  }

  if (error && !people) return <p className="error small">{error}</p>;
  if (!people) return <p className="muted small">Loading…</p>;

  const active = people.filter((p) => !p.archived);
  const archived = people.filter((p) => p.archived);

  return (
    <div className="people-panel">
      <p className="small">
        Everybody who can sign in. An RKH Google account that matches nobody here is refused, and signing in never adds
        anyone, so this list is the way in and the only way in.
        {!admin && ' Only a studio admin can change it.'}
      </p>

      <ul className="people-panel__list">
        {active.map((person) => (
          <li key={person.id} className="people-row">
            <PersonAvatar person={person} size={30} />
            {editing?.id === person.id ? (
              <form className="people-row__edit" onSubmit={saveEmail}>
                <input
                  type="email"
                  className="people-row__input"
                  value={editing.email}
                  autoFocus
                  disabled={busy}
                  aria-label={`Email address for ${person.name}`}
                  onChange={(e) => setEditing({ id: person.id, email: e.target.value })}
                />
                <button type="submit" className="btn btn--sm btn--primary" disabled={busy}>
                  Save
                </button>
                <button type="button" className="btn btn--sm" disabled={busy} onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </form>
            ) : (
              <>
                <span className="people-row__who">
                  {/* A record nobody has signed into yet reads as its address
                      in both slots, so the second line says what is missing
                      rather than repeating the first. */}
                  <span className="people-row__name">{person.name}</span>
                  <span className="people-row__email muted small">
                    {person.googleSub ? person.email : `${person.email} · has not signed in yet`}
                  </span>
                </span>
                {admin && (
                  <>
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy}
                      onClick={() => setEditing({ id: person.id, email: person.email ?? '' })}
                    >
                      Change address
                    </button>
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy}
                      onClick={() => setArchived(person, true)}
                    >
                      Archive
                    </button>
                  </>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      {admin && (
        <form className="people-panel__add" onSubmit={add}>
          <label htmlFor="people-add">Add someone</label>
          <div className="people-panel__add-row">
            <input
              id="people-add"
              type="email"
              className="people-row__input"
              placeholder="name@rkh.co.uk"
              value={draft}
              disabled={busy}
              onChange={(e) => setDraft(e.target.value)}
            />
            <button type="submit" className="btn btn--sm btn--primary" disabled={busy || !draft.trim()}>
              Add
            </button>
          </div>
          <p className="muted small">Their name and photo arrive from Google the first time they sign in.</p>
        </form>
      )}

      {archived.length > 0 && (
        <div className="people-panel__archived">
          <h4 className="people-panel__subhead">Archived</h4>
          <p className="muted small">
            They cannot sign in, and their name stays on the decks they made. Restoring lets them back in.
          </p>
          <ul className="people-panel__list">
            {archived.map((person) => (
              <li key={person.id} className="people-row people-row--archived">
                <PersonAvatar person={person} size={30} />
                <span className="people-row__who">
                  <span className="people-row__name">{person.name}</span>
                  <span className="people-row__email muted small">{person.email}</span>
                </span>
                {admin && (
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busy}
                    onClick={() => setArchived(person, false)}
                  >
                    Restore
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
